import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/dist/client/router'
import type { FunctionComponent } from 'react'
import { setDefaultBreakpoints } from 'react-socks'
import { useComparator } from '../../../hooks/useComparator'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import { IfHydrated } from '../../../libs/next/hydratation'
import type { API } from '../../../pages/api/v1/[handler]'
import { InternalLink } from '../../const/Links'
import type { DidYouKnowResult } from '../../data/DidYouKnowFragment'
import type { FooterResult } from '../../data/FooterFragment'
import type { SeoResult } from '../../data/SeoFragment'
import type { SocialResult } from '../../data/SocialFragment'
import { ComparatorBar } from '../comparator/ComparatorBar'
import { Footer } from '../layout/Footer'
import { Header } from '../layout/Header'
import { Breadcrumbs } from './Breadcrumbs'
import { CookieBanner } from './CookieBanner'
import { DidYouKnowBar } from './DidYouKnowBar'
import style from './Layout.module.sass'
import { SeoHead } from './SeoHead'

const api = createFetcher<API>('/api/v1/[handler]')

type LayoutProps = {
	didYouKnow?: DidYouKnowResult
	hasBreadcrumbs?: boolean
	breadcrumbsItems?: { id: string; title: string | undefined; url?: string }[]
	footer?: FooterResult
	socials: SocialResult[]
	children: React.ReactNode
	seo?: SeoResult | undefined
	url?: string | undefined
	titleSuffix?: string
}

export const Layout: FunctionComponent<LayoutProps> = ({
	children,
	didYouKnow,
	hasBreadcrumbs = false,
	breadcrumbsItems,
	socials,
	titleSuffix,
	seo,
	url,
	footer,
}) => {
	setDefaultBreakpoints([{ xs: 0 }, { s: 768 }, { m: 1200 }])

	const { removeAll, productsInComparator, removeById } = useComparator()

	const comparedProducts = useQuery({
		queryKey: ['compareProducts', productsInComparator],
		queryFn: async () => {
			return api('compare', { ids: productsInComparator })
		},
	})

	const loading = comparedProducts.isLoading

	const removeItemInComparator = (id: string) => {
		removeById(id)
	}

	const handleClear = () => {
		removeAll()
	}

	const sortedComparedProducts = productsInComparator
		.map((id) => comparedProducts.data?.listProduct.find((p) => p.id === id))
		.reverse()

	const router = useRouter()
	const excludedRoutes =
		router.asPath.includes(InternalLink.Comparator) ||
		router.asPath.includes(InternalLink.Wizard) ||
		router.asPath.includes(InternalLink.Login) ||
		router.asPath.includes(InternalLink.Register) ||
		router.asPath.includes(InternalLink.PasswordChange)

	return (
		<div className={style.wrapper}>
			<SeoHead seo={seo} url={url} titleSuffix={titleSuffix} />

			<CookieBanner />
			<Header />
			<div>
				{hasBreadcrumbs && (
					<div className={style.breadcrumbsWrapper}>
						<Breadcrumbs items={breadcrumbsItems} />
					</div>
				)}
				<div className={style.main}>{children}</div>
			</div>
			{productsInComparator.length > 0 && !excludedRoutes && (
				<IfHydrated
					yes={() => (
						<div className={style.comparatorBar}>
							<ComparatorBar
								comparedItems={sortedComparedProducts}
								removeAll={handleClear}
								removeItem={removeItemInComparator}
								isLoading={loading}
							/>
						</div>
					)}
				/>
			)}
			{productsInComparator.length === 0 && (
				<div className={style.didYouKnowBar}>{didYouKnow && <DidYouKnowBar text={didYouKnow.text} />}</div>
			)}
			{footer && (
				<div className={style.footer}>
					<Footer footer={footer} socials={socials} isCompact={router.asPath.includes(InternalLink.Wizard)} />
				</div>
			)}
		</div>
	)
}
